import { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { isTokenExpire } from "../utils/token";
import {
  Bars3Icon,
  ChartBarSquareIcon,
  BuildingLibraryIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowLeftStartOnRectangleIcon,
} from "@heroicons/react/24/outline";
import { STRING_FALSE, STRING_SIDEBAR_EXPANDED_TUTTIENGLISH, STRING_TRUE } from "../utils/const/string-const";
import { useTranslation } from "react-i18next";
import LayoutHeader from "./layout-header";
import { ReceiptTextIcon } from "lucide-react";
import { VERSION_TUTTIENGLISH } from "../utils/const/version-const";

const getSideBarStatus = () => {
  try {
    const sideBar = localStorage.getItem(STRING_SIDEBAR_EXPANDED_TUTTIENGLISH);
    if (sideBar !== null) {
      if (sideBar === STRING_FALSE) {
        return false;
      }
      if (sideBar === STRING_TRUE) {
        return true;
      }
    }
    return false;
  } catch (err) {
    return false;
  }
};

function Layout({ children }) {
  const { t } = useTranslation();
  const [expireToken, setExpireToken] = useState(true);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(getSideBarStatus());
  const history = useHistory();
  const location = useLocation();

  const sidebarNavigation = [
    {
      name: t("menu_dashboard"),
      href: "/dashboard",
      icon: ChartBarSquareIcon,
    },
    {
      name: t("menu_administracion"),
      href: "/administracion",
      icon: BuildingLibraryIcon,
      subMenu: [
        { name: t("menu_applicants"), href: "/administracion/list-applicant" },
        // { name: t("menu_insumos"), href: "/administracion/listar-insumo" },
        // { name: t("menu_presentacion"), href: "/administracion/listar-presentacion" },
        // { name: t("menu_unidad_medida"), href: "/administracion/listar-unidad-medida" },
        // { name: t("menu_categoria"), href: "/administracion/listar-categoria" },
      ],
    },
    // {
    //   name: t("menu_recetas"),
    //   href: "/recetas",
    //   icon: ReceiptTextIcon,
    //   subMenu: [
    //     { name: t("menu_listar_receta"), href: "/recetas/listar-receta" },
    //   ],
    // },
    {
      name: t("menu_salir"),
      href: "/salir",
      icon: ArrowLeftStartOnRectangleIcon,
    },
  ];

  const nextPage = (page) => {
    try {
      history.push(`${page}`);
    } catch (err) {
      console.log("nextPage err");
      console.log(err);
    }
  };

  useEffect(() => {
    const sideBar = localStorage.getItem(STRING_SIDEBAR_EXPANDED_TUTTIENGLISH);
    if (sideBar !== null) {
      if (sideBar === STRING_FALSE) {
        setIsSidebarExpanded(false);
      }
      if (sideBar === STRING_TRUE) {
        setIsSidebarExpanded(true);
      }
    }
    const resp = isTokenExpire();
    if (resp) {
      nextPage("/landing");
    } else {
      setExpireToken(false);
    }
  }, []);

  if (expireToken) {
    return <div />;
  }

  const handleToggleMenu = (name) => {
    setExpandedMenus((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleToggleSidebar = () => {
    localStorage.setItem(STRING_SIDEBAR_EXPANDED_TUTTIENGLISH, !isSidebarExpanded);
    setIsSidebarExpanded((prevState) => !prevState);
  };

  const isCurrentPage = (href, subMenu) => {
    if (subMenu) {
      return subMenu.some((sub) => location.pathname === sub.href);
    }
    return location.pathname === href;
  };

  const handleNavigate = (href) => {
    history.push(href);
    setIsMobileMenuOpen(false);
  };

  const renderMenuItem = (item, index) => {
    const isExpanded = expandedMenus[item.name];
    const isCurrent = isCurrentPage(item.href, item.subMenu);
    const hasSubMenu = item.subMenu && item.subMenu.length > 0;
    const isLogout = item.href === "/salir";

    const itemBaseClass =
      "flex items-center justify-between gap-2 rounded-xl cursor-pointer transition-all duration-200";
    const itemPadding = isSidebarExpanded ? "px-3 py-2.5" : "px-2 py-2.5 justify-center";

    let itemStateClass;
    if (isLogout) {
      itemStateClass = isCurrent
        ? "bg-red-50 text-red-700 ring-1 ring-red-200"
        : "text-slate-600 hover:bg-red-50 hover:text-red-700";
    } else if (isCurrent) {
      itemStateClass = "bg-indigo-600 text-white shadow-md shadow-indigo-600/20";
    } else {
      itemStateClass = "text-slate-600 hover:bg-indigo-50 hover:text-indigo-700";
    }

    return (
      <div key={index} className="mb-1">
        <div
          className={`${itemBaseClass} ${itemPadding} ${itemStateClass}`}
          onClick={() => {
            if (hasSubMenu) {
              handleToggleMenu(item.name);
            } else {
              handleNavigate(item.href);
            }
          }}
          title={item.name}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              if (hasSubMenu) {
                handleToggleMenu(item.name);
              } else {
                handleNavigate(item.href);
              }
            }
          }}
        >
          <div className={`flex items-center gap-3 min-w-0 ${!isSidebarExpanded ? "justify-center w-full" : "flex-1"}`}>
            {item.icon && (
              <item.icon
                className={`h-5 w-5 flex-shrink-0 ${isCurrent && !isLogout ? "text-white" : isLogout ? "text-red-500" : "text-indigo-500"}`}
              />
            )}
            {isSidebarExpanded && (
              <span className="font-medium text-sm truncate">{item.name}</span>
            )}
          </div>
          {hasSubMenu && isSidebarExpanded && (
            <div className="flex-shrink-0">
              {isExpanded ? (
                <ChevronDownIcon className="h-4 w-4 opacity-80" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 opacity-80" />
              )}
            </div>
          )}
        </div>

        {hasSubMenu && isExpanded && isSidebarExpanded && (
          <div className="ml-3 mt-1 pl-3 border-l-2 border-indigo-100 space-y-0.5">
            {item.subMenu.map((subItem, subIndex) => {
              const isSubCurrent = location.pathname === subItem.href;
              return (
                <div
                  key={subIndex}
                  className={`px-3 py-2 rounded-lg cursor-pointer text-sm font-medium transition-all duration-200 ${isSubCurrent
                    ? "bg-indigo-50 text-indigo-800"
                    : "text-slate-500 hover:bg-slate-50 hover:text-indigo-700"
                    }`}
                  onClick={() => handleNavigate(subItem.href)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleNavigate(subItem.href);
                    }
                  }}
                >
                  {subItem.name}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 text-slate-900 antialiased selection:bg-indigo-200 selection:text-indigo-950">
      {/* Mobile menu toggle */}
      <button
        type="button"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2.5 bg-white text-indigo-700 rounded-xl shadow-lg shadow-slate-900/10 border border-slate-200/80 hover:bg-indigo-50 transition"
        aria-expanded={isMobileMenuOpen}
        aria-label="Open menu"
      >
        <Bars3Icon className="h-6 w-6" />
      </button>

      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 flex flex-col h-full transition-all duration-300 ease-in-out
          bg-white border-r border-slate-200/80 shadow-xl shadow-slate-900/5 lg:shadow-none
          ${isSidebarExpanded ? "w-64 sm:w-72" : "w-[4.5rem]"}
          ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        {/* Brand */}
        <div className="flex items-center justify-between gap-2 p-4 border-b border-slate-100 min-h-[4.25rem]">
          <div className={`flex items-center gap-3 min-w-0 ${!isSidebarExpanded ? "justify-center w-full" : ""}`}>
            <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center shadow-md shadow-indigo-600/25">
              <span className="text-white font-bold text-sm tracking-tight">TE</span>
            </div>
            {isSidebarExpanded && (
              <div className="min-w-0">
                <p className="text-base font-bold text-indigo-900 tracking-tight truncate">
                  {t("te_brand")}
                </p>
                <p className="text-xs text-slate-500 truncate">{t("label_tuttie_english")}</p>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={handleToggleSidebar}
            className="hidden lg:flex flex-shrink-0 p-1.5 rounded-lg text-slate-500 hover:bg-indigo-50 hover:text-indigo-700 transition"
            aria-label={isSidebarExpanded ? "Collapse sidebar" : "Expand sidebar"}
          >
            <ChevronRightIcon
              className={`h-5 w-5 transition-transform duration-300 ${isSidebarExpanded ? "rotate-180" : ""}`}
            />
          </button>
        </div>

        <nav className="flex-1 p-3 sm:p-4 overflow-y-auto overflow-x-hidden">
          {sidebarNavigation.map((item, index) => renderMenuItem(item, index))}
        </nav>

        <div className="p-3 sm:p-4 border-t border-slate-100 bg-slate-50/50">
          {isSidebarExpanded ? (
            <p className="text-xs text-slate-500 text-center tabular-nums">
              v{VERSION_TUTTIENGLISH}
            </p>
          ) : (
            <p className="text-[10px] text-slate-400 text-center font-medium">v</p>
          )}
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <LayoutHeader userInfo={null} />

        <main className="flex-1 overflow-y-auto overflow-x-hidden bg-slate-50">
          <div className="min-h-full p-4 sm:p-6 lg:p-8">{children}</div>
        </main>
      </div>
    </div>
  );
}

export default Layout;
